import { ChevronRight } from "lucide-react";

import type { MatrixDevice } from "~/lib/matrix";
import { formatRelativeAgo } from "~/lib/relative-time";
import { ICON_STROKE } from "~/lib/icon";

export type OwnDevicesState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; devices: MatrixDevice[] };

/** The signed-in user's other sessions; an unverified row is itself the verify button. */
export function DeviceList({
  state,
  currentDeviceId,
  onVerify,
}: {
  state: OwnDevicesState;
  currentDeviceId: string | null;
  onVerify: (deviceId: string) => void;
}) {
  const others =
    state.status === "ready"
      ? state.devices
          .filter((device) => device.deviceId !== currentDeviceId && device.isDehydrated !== true)
          .sort((a, b) => (b.lastSeenTs ?? 0) - (a.lastSeenTs ?? 0))
      : [];

  return (
    <section aria-label="Other sessions" className="flex flex-col gap-2">
      <SectionLabel>Other sessions</SectionLabel>
      {state.status === "loading" && <Quiet>Loading sessions…</Quiet>}
      {state.status === "error" && <Quiet>Couldn't load your sessions.</Quiet>}
      {state.status === "ready" && others.length === 0 && <Quiet>No other sessions.</Quiet>}
      {others.length > 0 && (
        <ul className="-mx-2 flex flex-col">
          {others.map((device) => (
            <li key={device.deviceId}>
              <DeviceRow device={device} onVerify={() => onVerify(device.deviceId)} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function DeviceRow({ device, onVerify }: { device: MatrixDevice; onVerify: () => void }) {
  const name = device.displayName || shortDeviceId(device.deviceId);
  const detail = device.lastSeenTs
    ? `Active ${formatRelativeAgo(device.lastSeenTs)}`
    : `ID ${shortDeviceId(device.deviceId)}`;
  const body = (
    <span className="flex min-w-0 flex-1 flex-col">
      <span className="truncate text-base font-light text-foreground/90">{name}</span>
      <span className="truncate text-[13px] font-light text-muted-foreground">{detail}</span>
    </span>
  );

  if (device.isVerified) {
    return (
      <div className="flex min-h-14 items-center gap-3 px-2">
        {body}
        <span className="shrink-0 text-sm font-light text-muted-foreground">Verified</span>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onVerify}
      aria-label={`Verify ${name}`}
      className="flex min-h-14 w-full items-center gap-3 rounded-lg px-2 text-left outline-none transition-colors hover:bg-foreground/[0.03] active:bg-foreground/[0.06] focus-visible:shadow-neon-cyan"
    >
      {body}
      <span className="flex shrink-0 items-center gap-1 text-sm font-light text-foreground/80">
        Verify
        <ChevronRight
          aria-hidden
          size={16}
          strokeWidth={ICON_STROKE}
          className="text-muted-quiet"
        />
      </span>
    </button>
  );
}

export function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
      {children}
    </span>
  );
}

function Quiet({ children }: { children: React.ReactNode }) {
  return <p className="m-0 py-3 text-sm font-light text-muted-foreground">{children}</p>;
}

function shortDeviceId(id: string): string {
  return id.length > 6 ? `${id.slice(0, 4)}…${id.slice(-2)}` : id;
}
